// Extract fenced code blocks from a markdown text and package as a ZIP.
// Smart filename detection from common patterns.
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const LANG_TO_EXT = {
  html: 'html',
  htm: 'html',
  xml: 'xml',
  css: 'css',
  scss: 'scss',
  sass: 'scss',
  js: 'js',
  javascript: 'js',
  jsx: 'jsx',
  ts: 'ts',
  typescript: 'ts',
  tsx: 'tsx',
  json: 'json',
  py: 'py',
  python: 'py',
  sh: 'sh',
  bash: 'sh',
  md: 'md',
  markdown: 'md',
  yml: 'yml',
  yaml: 'yml',
  sql: 'sql',
  csv: 'csv',
  tsv: 'tsv',
  xlsx: 'xlsx',         // virtual — converted to real xlsx
  excel: 'xlsx',
  spreadsheet: 'xlsx',
  txt: 'txt',
  text: 'txt',
  plaintext: 'txt',
  env: 'env',
  dockerfile: 'Dockerfile',
  docker: 'Dockerfile',
};

const MIME_FOR_EXT = {
  html: 'text/html;charset=utf-8',
  css: 'text/css;charset=utf-8',
  js: 'text/javascript;charset=utf-8',
  jsx: 'text/javascript;charset=utf-8',
  ts: 'text/typescript;charset=utf-8',
  tsx: 'text/typescript;charset=utf-8',
  json: 'application/json;charset=utf-8',
  py: 'text/x-python;charset=utf-8',
  md: 'text/markdown;charset=utf-8',
  csv: 'text/csv;charset=utf-8',
  tsv: 'text/tab-separated-values;charset=utf-8',
  xml: 'application/xml;charset=utf-8',
  yml: 'application/x-yaml;charset=utf-8',
  yaml: 'application/x-yaml;charset=utf-8',
  sql: 'application/sql;charset=utf-8',
  sh: 'application/x-sh;charset=utf-8',
  txt: 'text/plain;charset=utf-8',
  env: 'text/plain;charset=utf-8',
};

const labelForExt = (ext) => {
  const map = {
    html: 'HTML',
    css: 'CSS',
    js: 'JS',
    jsx: 'JSX',
    ts: 'TS',
    tsx: 'TSX',
    json: 'JSON',
    py: 'PYTHON',
    md: 'MARKDOWN',
    csv: 'CSV',
    tsv: 'TSV',
    sql: 'SQL',
    yml: 'YAML',
    yaml: 'YAML',
    xml: 'XML',
    sh: 'SHELL',
    xlsx: 'EXCEL',
    txt: 'TEXTE',
    env: 'ENV',
    Dockerfile: 'DOCKERFILE',
  };
  return map[ext] || ext.toUpperCase();
};

// Try to detect a filename hint in a line preceding the code block,
// like "// index.html", "# app.py", "src/App.js:", etc.
const detectFilename = (precedingText) => {
  if (!precedingText) return null;
  const lines = precedingText.trim().split('\n').slice(-3); // last few lines
  for (const line of lines.reverse()) {
    const cleaned = line.trim().replace(/^[#/*\->\s]+/, '').replace(/[:`*\s]+$/, '');
    const m = cleaned.match(/([\w./-]+\.(?:html?|css|scss|jsx?|tsx?|json|py|sh|md|ya?ml|sql|csv|tsv|xlsx|txt|env))/i);
    if (m) return m[1];
  }
  return null;
};

export const extractCodeBlocks = (markdown) => {
  if (!markdown) return [];
  const blocks = [];
  const codeRe = /```([a-zA-Z0-9_+-]*)\n?([\s\S]*?)```/g;
  let m;
  let lastIdx = 0;
  while ((m = codeRe.exec(markdown)) !== null) {
    const lang = (m[1] || '').toLowerCase();
    const code = m[2];
    const preceding = markdown.slice(lastIdx, m.index);
    blocks.push({
      lang,
      code,
      ext: LANG_TO_EXT[lang] || 'txt',
      filename: detectFilename(preceding),
    });
    lastIdx = m.index + m[0].length;
  }
  return blocks;
};

const guessName = (block, idx) => {
  if (block.filename) return block.filename;
  const e = block.ext;
  if (e === 'html') return idx === 0 ? 'index.html' : `page-${idx + 1}.html`;
  if (e === 'css') return idx === 0 ? 'styles.css' : `styles-${idx + 1}.css`;
  if (e === 'js') return idx === 0 ? 'app.js' : `script-${idx + 1}.js`;
  if (e === 'py') return idx === 0 ? 'main.py' : `module-${idx + 1}.py`;
  if (e === 'csv') return `data-${idx + 1}.csv`;
  if (e === 'xlsx') return `data-${idx + 1}.xlsx`;
  if (e === 'json') return `data-${idx + 1}.json`;
  if (e === 'Dockerfile') return 'Dockerfile';
  return `file-${idx + 1}.${e}`;
};

// CSV parser: handles quoted cells, escaped quotes, commas in fields
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') { cell += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else cell += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(cell); cell = ''; }
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && next === '\n') i++;
        row.push(cell); cell = '';
        rows.push(row); row = [];
      } else cell += c;
    }
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => x !== ''));
};

// Convert a code block to a real .xlsx Blob using SheetJS
export const blockToXlsxBlob = (block) => {
  let aoa;
  if (block.ext === 'csv' || block.lang === 'csv' || block.lang === 'tsv') {
    aoa = parseCsv(block.code);
  } else if (block.lang === 'json' || block.ext === 'json' || block.lang === 'xlsx') {
    // Try JSON array of objects → rows
    const trimmed = block.code.trim();
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed) && parsed.length > 0) {
      if (Array.isArray(parsed[0])) {
        aoa = parsed;
      } else if (typeof parsed[0] === 'object' && parsed[0] !== null) {
        const headers = Array.from(new Set(parsed.flatMap((o) => Object.keys(o))));
        aoa = [headers, ...parsed.map((o) => headers.map((h) => o[h] ?? ''))];
      } else {
        aoa = parsed.map((v) => [v]);
      }
    } else {
      throw new Error('JSON doit être un tableau de lignes');
    }
  } else {
    throw new Error('Format non supporté pour XLSX (csv/json attendu)');
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Feuille1');
  const arrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

// Download a single code block as its native file
export const downloadBlock = (block, indexHint = 0) => {
  const name = guessName(block, indexHint);
  const mime = MIME_FOR_EXT[block.ext] || 'application/octet-stream';
  const blob = new Blob([block.code], { type: mime });
  saveAs(blob, name);
  return name;
};

// Download a CSV/JSON block converted to a real .xlsx file
export const downloadBlockAsXlsx = (block, indexHint = 0) => {
  const blob = blockToXlsxBlob(block);
  const baseName = block.filename ? block.filename.replace(/\.[^.]+$/, '') : `tableau-${indexHint + 1}`;
  saveAs(blob, `${baseName}.xlsx`);
  return `${baseName}.xlsx`;
};

export const downloadAsZip = async (blocks, baseName = 'ynx-export') => {
  if (!blocks || blocks.length === 0) {
    throw new Error('Aucun bloc de code à exporter');
  }
  const zip = new JSZip();
  const used = new Set();

  blocks.forEach((b, i) => {
    let name = guessName(b, i);
    // ensure unique names
    let unique = name;
    let n = 2;
    while (used.has(unique)) {
      const dot = name.lastIndexOf('.');
      unique = dot > 0 ? `${name.slice(0, dot)}-${n}${name.slice(dot)}` : `${name}-${n}`;
      n++;
    }
    used.add(unique);
    // For xlsx blocks, write the binary; otherwise plain text
    if (b.ext === 'xlsx') {
      try {
        const blob = blockToXlsxBlob(b);
        zip.file(unique, blob);
        return;
      } catch (err) {
        console.debug('clone: xlsx conversion failed, falling back to text', err);
      }
    }
    zip.file(unique, b.code);
  });

  const readme = `# ${baseName}\n\nGénéré par YNX AI le ${new Date().toLocaleString('fr-FR')}.\n\n## Fichiers\n${[...used].map((u) => `- ${u}`).join('\n')}\n`;
  zip.file('README.md', readme);

  const blob = await zip.generateAsync({ type: 'blob' });
  const fname = `${baseName}-${Date.now()}.zip`;
  saveAs(blob, fname);
  return fname;
};

// Expose label helper for the UI
export { labelForExt };

export const cloneUrl = async (url) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resp = await fetch(`${BACKEND_URL}/api/scrape`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Impossible de récupérer ${url} : HTTP ${resp.status} ${txt.slice(0, 200)}`);
  }
  return resp.json();
};
